import { Icon } from '@iconify/react';
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import config from "../config";
import { useAuth } from "../context/AuthContext";
import { signInWithPopup } from "firebase/auth";
import { auth as firebaseAuth, googleProvider, facebookProvider } from "../firebase";

const SignInLayer = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  const [logoUrl, setLogoUrl] = useState('/assets/images/logo.png');
  const [authBgUrl, setAuthBgUrl] = useState('/assets/images/auth/auth-img.png');

  useEffect(() => {
    fetch(`${config.API_BASE_URL}/api/settings`)
        .then(res => res.json())
        .then(data => {
            if (data.logo_url) setLogoUrl(`${config.API_BASE_URL}${data.logo_url}`);
            if (data.auth_bg_url) setAuthBgUrl(`${config.API_BASE_URL}${data.auth_bg_url}`);
        })
        .catch(err => console.error('Failed to fetch settings:', err));
  }, []);

  // APP_TYPE Check
  const APP_TYPE = process.env.REACT_APP_APP_TYPE || 'dev';

  const handleGoogleLogin = async () => {
    if (APP_TYPE === 'admin') {
      setError("Google Login is disabled for Administrators.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      const result = await signInWithPopup(firebaseAuth, googleProvider);
      const idToken = await result.user.getIdToken();

      const response = await fetch(`${config.API_BASE_URL}/api/auth/google-login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token: idToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Google Login failed");
      }

      // Role-based Access Control Check
      const userRole = data.user.role;
      const userPermissions = data.user.permissions || [];
      
      const hasAdminAccess = userRole === 'admin' || userRole === 'employee' || userPermissions.includes('admin_access');

      if (APP_TYPE === 'business' && hasAdminAccess) {
         throw new Error("Admins and Employees cannot log in to the Business Dashboard. Please use the Admin Portal.");
      }
      
      if (APP_TYPE === 'admin' && !hasAdminAccess) {
         throw new Error("Access Denied. This portal is for Administrators and Employees only.");
      }

      auth.login(data.token, data.user);
      navigate("/");

    } catch (err) {
      console.error(err);
      // Ignore if user closed the popup manually
      if (err.code === 'auth/popup-closed-by-user') {
        return;
      }
      setError(err.message || "Google Sign-In Failed");
    } finally {
      setLoading(false);
    }
  };

  const handleFacebookLogin = async () => {
    if (APP_TYPE === 'admin') {
      setError("Facebook Login is disabled for Administrators.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      const result = await signInWithPopup(firebaseAuth, facebookProvider);
      const idToken = await result.user.getIdToken();

      const response = await fetch(`${config.API_BASE_URL}/api/auth/facebook-login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token: idToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Facebook Login failed");
      }

      // Role-based Access Control Check
      const userRole = data.user.role;
      const userPermissions = data.user.permissions || [];
      
      const hasAdminAccess = userRole === 'admin' || userRole === 'employee' || userPermissions.includes('admin_access');

      if (APP_TYPE === 'business' && hasAdminAccess) {
         throw new Error("Admins and Employees cannot log in to the Business Dashboard. Please use the Admin Portal.");
      }
      
      if (APP_TYPE === 'admin' && !hasAdminAccess) {
         throw new Error("Access Denied. This portal is for Administrators and Employees only.");
      }

      auth.login(data.token, data.user);
      navigate("/");

    } catch (err) {
      console.error(err);
      // Ignore if user closed the popup manually
      if (err.code === 'auth/popup-closed-by-user') {
        return;
      }
      setError(err.message || "Facebook Sign-In Failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await fetch(`${config.API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Login failed");
      }

      // Role-based Access Control Check
      const userRole = data.user.role;
      const userPermissions = data.user.permissions || [];
      
      const hasAdminAccess = userRole === 'admin' || userRole === 'employee' || userPermissions.includes('admin_access');

      if (APP_TYPE === 'business' && hasAdminAccess) {
         throw new Error("Admins and Employees cannot log in to the Business Dashboard. Please use the Admin Portal.");
      }
      
      if (APP_TYPE === 'admin' && !hasAdminAccess) {
         throw new Error("Access Denied. This portal is for Administrators and Employees only.");
      }

      // Store token and user info
      auth.login(data.token, data.user);

      // Redirect to dashboard or employees page
      navigate("/");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className='auth bg-base d-flex flex-wrap'>
      <div className='auth-left d-lg-block d-none'>
        <div className='d-flex align-items-center flex-column h-100 justify-content-center'>
          <img src={authBgUrl} alt='' style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      </div>
      <div className='auth-right py-32 px-24 d-flex flex-column justify-content-center'>
        <div className='max-w-464-px mx-auto w-100'>
          <div className='text-center'>
            <Link to='/' className='mb-40 max-w-290-px d-inline-block'>
              <img 
                src={logoUrl} 
                alt='' 
                style={{ maxHeight: '55px', width: 'auto' }} 
                onError={(e) => {
                  e.target.onerror = null; 
                  e.target.src = '/assets/images/logo.png';
                }}
              />
            </Link>
            <h4 className='mb-12'>Sign In to your Account</h4>
            <p className='mb-32 text-secondary-light text-lg'>
              Welcome back! please enter your detail
            </p>
          </div>
          
          {error && (
            <div className="alert alert-danger mb-3" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className='icon-field mb-16'>
              <span className='icon top-50 translate-middle-y'>
                <Icon icon='mage:email' />
              </span>
              <input
                type='email'
                className='form-control h-56-px bg-neutral-50 radius-12'
                placeholder='Email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className='position-relative mb-20'>
              <div className='icon-field'>
                <span className='icon top-50 translate-middle-y'>
                  <Icon icon='solar:lock-password-outline' />
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className='form-control h-56-px bg-neutral-50 radius-12'
                  id='your-password'
                  placeholder='Password'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <span
                className={`toggle-password ${showPassword ? 'ri-eye-line' : 'ri-eye-off-line'} cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light`}
                onClick={() => setShowPassword(!showPassword)}
              />
            </div>
            <div className=''>
              <div className='d-flex justify-content-between gap-2'>
                <div className='form-check style-check d-flex align-items-center'>
                  <input
                    className='form-check-input border border-neutral-300'
                    type='checkbox'
                    defaultValue=''
                    id='remeber'
                  />
                  <label className='form-check-label' htmlFor='remeber'>
                    Remember me{" "}
                  </label>
                </div>
                {APP_TYPE !== 'admin' && APP_TYPE !== 'employee' && (
                  <Link to='/forgot-password' className='text-primary-600 fw-medium'>
                    Forgot Password?
                  </Link>
                )}
              </div>
            </div>
            <button
              type='submit'
              className='btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 mt-32'
              disabled={loading}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
            
            {APP_TYPE !== 'admin' && (
              <>
                <div className='mt-32 center-border-horizontal text-center'>
                  <span className='bg-base z-1 px-4'>Or sign in with</span>
                </div>
                <button
                  type='button'
                  className='btn btn-outline-light text-neutral-600 w-100 mt-32 d-flex align-items-center justify-content-center gap-2'
                  onClick={handleGoogleLogin}
                  disabled={loading}
                >
                  <Icon icon='logos:google-icon' width="20" height="20" />
                  Sign in with Google
                </button>
                <button
                  type='button'
                  className='btn btn-outline-light text-neutral-600 w-100 mt-16 d-flex align-items-center justify-content-center gap-2'
                  onClick={handleFacebookLogin}
                  disabled={loading}
                >
                  <Icon icon='logos:facebook' width="20" height="20" />
                  Sign in with Facebook
                </button>
                <div className='mt-32 text-center text-sm'>
                  <p className='mb-0'>
                    Don’t have an account?{" "}
                    <Link to='/sign-up' className='text-primary-600 fw-semibold'>
                      Sign Up
                    </Link>
                  </p>
                </div>
              </>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default SignInLayer;
